import { isSectionRight } from '@shared/services/rights';

import type { SectionRight } from '@shared/services/rights';

/**
 * Single source of truth: which section right unlocks which landing route.
 *
 * Consumed by BOTH the navigation sidebar (entry visibility) and the
 * {@link sectionAuthorizationGuard}'s "first authorized section" fallback, plus
 * route `data.requiredRights` (via {@link requiredRightsForPath}). Because all
 * three derive from this one list, drift between menu, fallback, and route gate
 * is structurally impossible.
 *
 * Order = fallback priority (first held right wins).
 *
 * SECURITY: OR semantics — holding the listed right grants the section.
 *
 * Aparté-embed sections (catalog products/parameters, settings, reports) are
 * gated purely on rights (no feature-flag layer). Because the backend collapses
 * several legacy rights onto `USERRIGHT_PARAMACCES`, products/settings reuse the
 * existing `ParamAccess` right rather than distinct aliases.
 */
export interface SectionAccess {
  readonly right: SectionRight;
  readonly path: string;
}

export const SECTION_ACCESS: readonly SectionAccess[] = [
  { right: 'ContreMarque', path: '/dashboard' },
  { right: 'SaleAccess', path: '/sales' },
  { right: 'OrderAccess', path: '/orders' },
  { right: 'CustomerAccess', path: '/customers' },
  { right: 'CatalogAcces', path: '/events' },
  { right: 'PlanManagement', path: '/catalog-plan' },
  { right: 'PriceEdit', path: '/price-list' },
  { right: 'ParamAccess', path: '/subscriptions' },
  { right: 'ParamAccess', path: '/mise-en-vente' },
  // Aparté-embed sections. Placed after the primary sections so the fallback
  // "first authorized section" priority is unchanged for existing rights.
  { right: 'ParamAccess', path: '/catalog/products' },
  { right: 'ResaleAccess', path: '/catalog/events/parameters' },
  { right: 'ParamAccess', path: '/settings' },
  { right: 'MockupAccess', path: '/settings/mockup' },
  { right: 'ParamAccess', path: '/settings/channels' },
  { right: 'ResaleAccess', path: '/settings/resale-configuration' },
  { right: 'ReportEditAccess', path: '/reports' },
] as const;

/**
 * The app's preferred post-authentication landing route. Used by the root
 * redirect ({@link Routes} `''`) and the `redirectIfAuthenticatedGuard`. Kept
 * here, alongside {@link HOME_URLS}, so the landing route is structurally
 * guaranteed to be in the graceful-fallback set — the landing and the fallback
 * eligibility can never drift.
 *
 * Lands a user with `ContreMarque` on the dashboard; a user lacking it but
 * holding another section right is redirected by the guard to their first
 * authorized section; a user with no section rights reaches /access-denied.
 */
export const DEFAULT_HOME_URL = '/dashboard';

/**
 * URLs that trigger the graceful "redirect to first authorized section"
 * fallback (rule 4a). Any other denied URL goes straight to /access-denied.
 * Typed as `readonly string[]` so `.includes(currentUrl)` needs no cast.
 */
export const HOME_URLS: readonly string[] = ['/', DEFAULT_HOME_URL];

export const ACCESS_DENIED_URL = '/access-denied';

/**
 * Informational landing shown when a tenant-less request cannot be resolved to a
 * workspace (see `resolveEffectiveTenant` / `tenantRootRedirectGuard`). Reserved
 * (not tenant-scoped) and reachable unauthenticated — it is the only surface a
 * prod visitor with no session and no URL tenant can reach, since `acr_values`
 * (the IdP's sole tenant selector) cannot be formed without a known tenant.
 */
export const SELECT_WORKSPACE_URL = '/select-workspace';

/**
 * Shape of the `data` block this app's section routes declare. Authored on
 * routes with `satisfies SectionRouteData` for compile-time safety; read back
 * through {@link normalizeRights} (the runtime `data` type is Angular's untyped
 * `Data`).
 */
export interface SectionRouteData {
  readonly requiredRights?: SectionRight | readonly SectionRight[];
}

/**
 * Normalize a route's raw `data['requiredRights']` (single right | list |
 * absent) into a typed list. Uses the {@link isSectionRight} guard so no `as`
 * cast is needed and unknown values are dropped (parse, don't validate).
 */
export function normalizeRights(value: unknown): readonly SectionRight[] {
  if (Array.isArray(value)) {
    return value.filter(isSectionRight);
  }
  return isSectionRight(value) ? [value] : [];
}

/**
 * Required rights for a route path, sourced from {@link SECTION_ACCESS}. Used to
 * stamp route `data.requiredRights` so it can never drift from the menu/fallback
 * definition. An unlisted path is ungated (empty list → allow).
 */
export function requiredRightsForPath(path: string): readonly SectionRight[] {
  const entry = SECTION_ACCESS.find((e) => e.path === path);
  return entry ? [entry.right] : [];
}

/**
 * The OR-semantics authorization check, shared by every section gate. Holding
 * ANY one of the required rights grants access; an empty `required` is ungated
 * (always allowed). Centralized here so the route guard, the navigation
 * sidebar, and the command palette can never apply divergent rules.
 *
 * SECURITY: this is the single definition of "is this allowed?" — change it
 * here, not at the call sites.
 */
export function holdsAnyRight(
  required: readonly SectionRight[],
  rights: readonly SectionRight[],
): boolean {
  return required.length === 0 || required.some((r) => rights.includes(r));
}

/**
 * Whether a section route is visible/reachable for the given rights. Ungated
 * paths (not in {@link SECTION_ACCESS}) are always allowed.
 */
export function isPathAllowed(path: string, rights: readonly SectionRight[]): boolean {
  return holdsAnyRight(requiredRightsForPath(path), rights);
}

/**
 * The first section (in {@link SECTION_ACCESS} priority order) the user is
 * allowed to see, or `undefined` when they can see nothing. `undefined` is a
 * self-explanatory "no section available" lookup result — the caller maps it to
 * {@link ACCESS_DENIED_URL}.
 */
export function firstAuthorizedPath(rights: readonly SectionRight[]): string | undefined {
  return SECTION_ACCESS.find((e) => rights.includes(e.right))?.path;
}
